import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { zhCN, patternsZhCN } from './translations';

export const LOCALE_KEY = 'lexilab.locale';
export const locales = Object.freeze({ EN: 'en', ZH_CN: 'zh-CN' });

const I18nContext = createContext(null);
const originalText = new WeakMap();
const expectedText = new WeakMap();
const expectedAttributes = new WeakMap();
const translatedAttributes = ['placeholder', 'title', 'aria-label', 'alt'];

const isTranslationSkipped = (node) => {
  const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  return Boolean(element?.closest?.('[data-i18n-skip]'));
};

const interpolate = (value, params = {}) => String(value).replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);

export const translateText = (text, locale, params) => {
  const source = String(text ?? '');
  if (locale !== locales.ZH_CN || !source.trim()) return interpolate(source, params);
  const leading = source.match(/^\s*/)?.[0] || '';
  const trailing = source.match(/\s*$/)?.[0] || '';
  const clean = source.trim();
  let translated = zhCN[clean];
  if (!translated) {
    for (const [pattern, replacement] of patternsZhCN) {
      if (pattern.test(clean)) {
        translated = clean.replace(pattern, replacement);
        break;
      }
    }
  }
  return `${leading}${interpolate(translated || clean, params)}${trailing}`;
};

export const apiMessage = (payload, locale, fallback = 'Something went wrong.') => {
  if (!payload) return translateText(fallback, locale);
  const code = payload.code || payload.message_code;
  const codeKey = code ? `api.${code}` : null;
  const candidate = (
    locale === locales.ZH_CN && codeKey && zhCN[codeKey]
  ) || payload.message || payload.error || fallback;
  return translateText(candidate, locale, payload.params || {});
};

export const localizeNode = (root, locale) => {
  if (!root || isTranslationSkipped(root)) return;
  const elements = root.nodeType === Node.ELEMENT_NODE ? [root, ...root.querySelectorAll('*')] : [];
  elements.forEach((element) => {
    if (isTranslationSkipped(element)) return;
    translatedAttributes.forEach((attribute) => {
      if (!element.hasAttribute?.(attribute)) return;
      const marker = `data-i18n-original-${attribute}`;
      if (!element.hasAttribute(marker)) element.setAttribute(marker, element.getAttribute(attribute));
      const source = element.getAttribute(marker);
      const translated = translateText(source, locale);
      const expected = expectedAttributes.get(element) || new Map();
      expected.set(attribute, translated);
      expectedAttributes.set(element, expected);
      element.setAttribute(attribute, translated);
    });
  });

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    if (isTranslationSkipped(node)) return;
    if (['SCRIPT', 'STYLE', 'TEXTAREA'].includes(node.parentElement?.tagName)) return;
    if (!originalText.has(node)) originalText.set(node, node.nodeValue);
    const translated = translateText(originalText.get(node), locale);
    expectedText.set(node, translated);
    node.nodeValue = translated;
  });
};

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => localStorage.getItem(LOCALE_KEY) || locales.EN);
  const setLocale = useCallback((next) => {
    const normalized = next === locales.ZH_CN ? locales.ZH_CN : locales.EN;
    localStorage.setItem(LOCALE_KEY, normalized);
    setLocaleState(normalized);
  }, []);
  const t = useCallback((key, params) => translateText(key, locale, params), [locale]);
  const message = useCallback((payload, fallback) => apiMessage(payload, locale, fallback), [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
    axios.defaults.headers.common['Accept-Language'] = locale;
    localizeNode(document.body, locale);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          const node = mutation.target;
          if (isTranslationSkipped(node)) return;
          if (node.nodeValue === expectedText.get(node)) return;
          originalText.set(node, node.nodeValue);
          const translated = translateText(node.nodeValue, locale);
          expectedText.set(node, translated);
          node.nodeValue = translated;
          return;
        }
        if (mutation.type === 'attributes') {
          const element = mutation.target;
          if (isTranslationSkipped(element)) return;
          const attribute = mutation.attributeName;
          if (element.getAttribute(attribute) === expectedAttributes.get(element)?.get(attribute)) return;
          const source = element.getAttribute(attribute) || '';
          element.setAttribute(`data-i18n-original-${attribute}`, source);
          const translated = translateText(source, locale);
          const expected = expectedAttributes.get(element) || new Map();
          expected.set(attribute, translated);
          expectedAttributes.set(element, expected);
          element.setAttribute(attribute, translated);
          return;
        }
        const { addedNodes } = mutation;
        addedNodes.forEach((node) => {
        if (isTranslationSkipped(node)) return;
        if (node.nodeType === Node.TEXT_NODE) {
          if (!originalText.has(node)) originalText.set(node, node.nodeValue);
          const translated = translateText(originalText.get(node), locale);
          expectedText.set(node, translated);
          node.nodeValue = translated;
        } else if (node.nodeType === Node.ELEMENT_NODE) localizeNode(node, locale);
        });
      });
    });
    observer.observe(document.body, { childList: true, characterData: true, attributes: true, attributeFilter: translatedAttributes, subtree: true });
    return () => observer.disconnect();
  }, [locale]);

  useEffect(() => {
    const nativeAlert = window.alert;
    const nativeConfirm = window.confirm;
    window.alert = (value) => nativeAlert(translateText(value, locale));
    window.confirm = (value) => nativeConfirm(translateText(value, locale));
    return () => {
      window.alert = nativeAlert;
      window.confirm = nativeConfirm;
    };
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t, apiMessage: message }), [locale, setLocale, t, message]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside I18nProvider');
  return value;
};
